import * as THREE from 'three'
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js'

/** Opaque decoration is baked in prop order, retaining the same adaptive prefix.
 * Borrowed factory materials and transparent sorting remain untouched. */
export class DioramaStaticBatch {
  readonly root = new THREE.Group()
  private batches: Array<{ mesh: THREE.Mesh; ends: number[] }> = []
  private hidden: THREE.Mesh[] = []

  constructor(props: readonly THREE.Object3D[]) {
    this.root.name = 'BatchedMiniatureDecoration'
    const groups = new Map<string, { material: THREE.Material; cast: boolean; receive: boolean; pieces: THREE.BufferGeometry[]; ends: number[] }>()
    props.forEach((prop, index) => {
      prop.updateWorldMatrix(true, true)
      prop.traverse(object => {
        if (!(object instanceof THREE.Mesh) || object instanceof THREE.SkinnedMesh || object instanceof THREE.InstancedMesh
          || !object.visible || Array.isArray(object.material) || object.material.transparent || object.morphTargetInfluences) return
        const key = `${object.material.uuid}:${object.castShadow}:${object.receiveShadow}`
        let group = groups.get(key)
        if (!group) {
          group = { material: object.material, cast: object.castShadow, receive: object.receiveShadow, pieces: [], ends: Array(props.length).fill(0) }
          groups.set(key, group)
        }
        const geometry = object.geometry.index ? object.geometry.toNonIndexed() : object.geometry.clone()
        geometry.applyMatrix4(object.matrixWorld)
        if (!geometry.attributes.normal) geometry.computeVertexNormals()
        if (!geometry.attributes.uv) geometry.setAttribute('uv', new THREE.Float32BufferAttribute(new Float32Array(geometry.attributes.position.count * 2), 2))
        group.pieces.push(geometry)
        group.ends[index] += geometry.attributes.position.count
        object.visible = false
        this.hidden.push(object)
      })
    })
    for (const group of groups.values()) {
      const geometry = mergeGeometries(group.pieces, false)
      group.pieces.forEach(piece => piece.dispose())
      if (!geometry) throw new Error('Incompatible miniature decoration geometry')
      const mesh = new THREE.Mesh(geometry, group.material)
      mesh.castShadow = group.cast
      mesh.receiveShadow = group.receive
      for (let i = 1; i < group.ends.length; i++) group.ends[i] += group.ends[i - 1]
      this.batches.push({mesh, ends: group.ends})
      this.root.add(mesh)
    }
    this.setVisibleProps(props.length)
  }

  setVisibleProps(count: number): void {
    for (const {mesh, ends} of this.batches) {
      const vertices = ends[Math.min(ends.length, Math.max(0, Math.floor(count))) - 1] ?? 0
      mesh.geometry.setDrawRange(0, vertices)
      mesh.visible = vertices > 0
    }
  }

  dispose(): void {
    this.batches.forEach(({mesh}) => mesh.geometry.dispose())
    this.hidden.forEach(mesh => { mesh.visible = true })
    this.batches = []
    this.hidden = []
    this.root.removeFromParent()
    this.root.clear()
  }
}
